import React, { useState } from 'react';
import { useAppConstantsContext } from '@/context/AppConstants';
import { SectionBaseProps, PhoneRowProps } from '@/reports/contact/Interfaces';
import { RowCard } from '@/reports/contact/RowCard';
import { ShowMoreToggle } from '@/reports/contact/components';
import {
  HeaderContainer,
  HeaderTextContainer,
  DataContainer,
  TextContainer,
} from '@/reports/contact/sections/ContactEmailSection/ContactEmailSection';
import {
  ReportSection,
  openReportInNewTab,
  WarningFooter,
} from '@/reports/index';
import { commonTypes } from '@/reports/types';
import { Stack, Text, useTheme } from '@/theme/index';
import { formatDateFromReport } from '@/utils/formatDateFromReport/formatDateFromReport';
import { formatCountryPhone } from '@/utils/index';

const PhoneThreshold = 3;

export interface ContactPhoneSectionProps extends SectionBaseProps {
  /** The number of phone */
  phonesCount: number;
  /** The list of phone */
  phoneList: commonTypes.Phones;
}

function phoneTypeList(
  expand: boolean,
  phoneList: commonTypes.Phone[]
): commonTypes.Phone[] {
  return expand ? phoneList : phoneList.slice(0, PhoneThreshold);
}

/**
 * This component displays the line of the phone table.
 * For each phone a line is created which may or may not include the Last Seen attribute as a variant.
 */
function PhoneRow({ permalink, phone }: PhoneRowProps) {
  const theme = useTheme();
  const {
    links: { baseUrl },
  } = useAppConstantsContext();
  const openReport = () => {
    openReportInNewTab(
      {
        phone: phone.number || '',
        searchType: 'phone',
      },
      baseUrl
    );
  };

  const phoneNumber = formatCountryPhone(phone.number || '');

  return (
    <RowCard
      permalink={permalink}
      item={phone}
      value={phoneNumber}
      component={
        <DataContainer>
          <TextContainer>
            <Stack
              spacing={1}
              direction="column"
              alignItems="left"
              sx={(theme) => ({
                mr: theme.spacing(0.5),
              })}
            >
              <Text
                color={(theme) => theme.palette.primary.main}
                sx={{ cursor: 'pointer' }}
                onClick={openReport}
              >
                {phoneNumber}
              </Text>
              {phone?.meta?.last_seen_date?.full && (
                <Text fontSize={theme.typography.caption.fontSize}>
                  Last seen:{' '}
                  {formatDateFromReport(phone?.meta?.last_seen_date?.full)}
                </Text>
              )}
            </Stack>
          </TextContainer>
        </DataContainer>
      }
    />
  );
}

/**
 * This component displays the phone section of the contact report.
 */
export function ContactPhoneSection({
  phonesCount,
  personName,
  phoneList,
  permalink,
}: ContactPhoneSectionProps) {
  const [expandPhones, setExpandPhones] = useState(false);
  const expandPhonesHandler = () => {
    setExpandPhones(!expandPhones);
  };
  const displayedPhonesList = phoneTypeList(expandPhones, phoneList);

  return (
    <ReportSection
      title="Possible Phone Numbers"
      sectionId="phone-numbers-section"
      hasContent={phonesCount > 0}
      personName={personName}
      FooterComponent={<WarningFooter />}
    >
      {phoneList.length > 0 && (
        <HeaderContainer>
          <HeaderTextContainer>Phones ({phonesCount})</HeaderTextContainer>
        </HeaderContainer>
      )}
      {phoneList.length > 0 &&
        displayedPhonesList.map((phone) => (
          <PhoneRow key={phone.number} permalink={permalink} phone={phone} />
        ))}
      <ShowMoreToggle
        list={phoneList}
        expand={expandPhones}
        expandHandler={expandPhonesHandler}
      />
    </ReportSection>
  );
}
